/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import { Else, If, Then } from "react-if";
import { Box, FlexContainer } from "@nordcloud/gnui";
import {
  NotificationGroupSortFields,
  SortOrder,
  NotificationGroup,
} from "~/generated/graphql";
import { LoaderWrap } from "~/components/Loader";
import { NoData } from "~/components/NoData";
import { Pagination } from "~/components/Pagination";
import { SortSelector } from "~/components/SortSelector";
import { TableWrapper } from "~/components/Tables";
import { TABLE_LABELS } from "~/constants";
import { useQueryState } from "~/hooks";
import { isEmpty, isNotNil } from "~/tools";
import { useGetNotificationGroups } from "../hooks/useGetNotificationGroups/useGetNotificationGroups";
import { columns } from "./NotificationGroupsTableColumns";

type QuerySort = {
  field: NotificationGroupSortFields;
  order: SortOrder;
};

const sortOptions = [
  {
    value: NotificationGroupSortFields.Name,
    label: "Notification Group Name",
  },
  {
    value: NotificationGroupSortFields.CreatedAt,
    label: TABLE_LABELS.createdAt,
  },
  {
    value: NotificationGroupSortFields.UpdatedAt,
    label: TABLE_LABELS.updatedAt,
  },
  {
    value: NotificationGroupSortFields.UpdatedBy,
    label: TABLE_LABELS.updatedBy,
  },
];

export function NotificationGroupsTable() {
  const { queryState, updateQueryState } = useQueryState<QuerySort>();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const handleSortDirection = (order: SortOrder) => {
    updateQueryState({
      ...queryState,
      order,
    });
  };

  const handleSortField = (field: NotificationGroupSortFields) => {
    updateQueryState({
      ...queryState,
      field,
    });
  };

  const { data, loading, count } = useGetNotificationGroups({
    page: queryState.page,
    limit: queryState.limit,
    sort: {
      field: queryState.field ?? NotificationGroupSortFields.Name,
      order: queryState.order ?? SortOrder.Asc,
    },
  });

  return (
    <>
      <Box boxStyle="lightGrey" spacing="spacing04">
        <FlexContainer justifyContent="space-between">
          <SortSelector<NotificationGroupSortFields>
            options={sortOptions}
            value={queryState.field ?? NotificationGroupSortFields.Name}
            direction={queryState.order ?? SortOrder.Asc}
            onSelect={handleSortField}
            onSortChange={handleSortDirection}
          />
        </FlexContainer>
      </Box>
      <LoaderWrap loading={loading} inContent>
        <If condition={isEmpty(data)}>
          <Then>
            <NoData hasIcon message={"There are no Notification Groups."} />
          </Then>
          <Else>
            <>
              <TableWrapper<Partial<NotificationGroup>>
                loading={loading}
                data={data.filter(isNotNil)}
                columns={columns()}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                stripped
              />
              <Pagination count={count} />
            </>
          </Else>
        </If>
      </LoaderWrap>
    </>
  );
}
